import React, { useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import {
  CBadge,
  CButton,
  CCard,
  CFormSelect,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheckCircle,
  cilCloudDownload,
  cilCloudUpload,
  cilFile,
  cilPeople,
  cilSync,
  cilWarning,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  ImportFileBar,
  ImportUploadStep,
  InfoBox,
  MiniStepper,
  StatTile,
  tintStyle,
} from 'src/components/common/ImportWizard'
import {
  MATCH_META,
  buildPlayerIndex,
  readPlayerCells,
  resolvePlayer,
  takePlayerColumns,
} from 'src/components/common/playerMatching'
import { POSITION_META, hasScores } from './scorecardConfig'
import {
  DisplayValue,
  RowScores,
  parseInningValue,
  parseStatCells,
  readStatCells,
  takeInningsColumn,
  takeStatColumns,
} from './scorecardCsv'

const WIZARD_STEPS = ['Upload File', 'Validate Players', 'Review & Import']

const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  unmatched_player: {
    label: 'Player Not Found',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  ambiguous_player: {
    label: 'Several Players Match',
    color: 'warning',
    icon: cilWarning,
    accent: '#b45309',
  },
  duplicate: { label: 'Duplicate Player', color: 'warning', icon: cilWarning, accent: '#b45309' },
  no_stats: { label: 'No Scores In Row', color: 'warning', icon: cilWarning, accent: '#b45309' },
  unknown_inning: {
    label: 'Innings Not Recognised',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
}

// Players come off the game's own list rather than off this fixture's squad, so the name to show
// is `full_name` and the position is `position` - the scorecard rows spell both differently.
const playerLabel = (player) => {
  const position = POSITION_META[player?.position]
  return position ? `${player.full_name} (${position.label})` : player?.full_name || ''
}

// Turns a parsed sheet into the rows the wizard validates: which header is the player, which
// headers are stats this game scores, and what is left over. A real match export carries every
// column on the grid whether the innings used it or not, so a column that is blank the whole way
// down is dropped rather than shown as 24 empty cells to scroll past.
const readSheet = (json, rawHeaders, squadColumns, multiInnings) => {
  const pool = [...rawHeaders]
  const playerColumns = takePlayerColumns(pool)
  if (!playerColumns.found) {
    return { error: 'This file has no player name or player ID column.' }
  }

  // Claimed only on a fixture that has more than one innings. A one-innings fixture has nothing
  // to tell apart, so the column stays in the pool and is reported as ignored like any other.
  const inningsColumn = multiInnings ? takeInningsColumn(pool) : ''

  const detectedColumns = takeStatColumns(pool, json, squadColumns)

  if (!detectedColumns.length) {
    return {
      error: 'None of the columns in this file have anything to import into this scorecard.',
    }
  }

  const baseRows = json
    .map((r) => ({
      ...readPlayerCells(r, playerColumns),
      csv_inning: inningsColumn ? String(r[inningsColumn] ?? '').trim() : '',
      cells: readStatCells(r, detectedColumns),
      manual_id: '',
      manual_inning: '',
    }))
    // Rows with no player on them are the blank lines spreadsheets leave behind, not data.
    .filter((r) => r.csv_name !== '' || r.csv_player_id !== '')

  if (!baseRows.length) return { error: 'This file has no rows to import.' }

  return { baseRows, detectedColumns, inningsColumn, ignoredHeaders: pool }
}

// Re-run over the whole file after every mapping change - which player a row points at decides
// whether some other row is now a duplicate, so a single row cannot be re-checked on its own.
//
// Rows are matched against every player in the game, not only the ones already picked for this
// fixture: a match export names whoever played, and an admin importing one should not have to go
// and build the squad by hand first. A player who is not in the squad yet is added to it as part
// of the import, which is also what creates the scorecard row their figures are written to.
//
// `innings` is what the fixture actually has: one entry for a 1-day match, two for a 2-day one,
// each carrying the scorecard rows already saved for it. The two innings of a 2-day match are
// separate rows for the same player, so a player counts as duplicated only within one innings.
const buildValidatedRows = (baseRows, statColumns, innings, playerIndex, columns) => {
  const claimed = new Set()
  const single = innings.length === 1 ? innings[0] : null

  return baseRows.map((base, i) => {
    // One innings means every row belongs to it, whatever the file says. Otherwise the innings
    // comes off the file, or off the select the admin used to place a row it could not read.
    const wanted = single ? single.value : parseInningValue(base.manual_inning || base.csv_inning)
    const inning = innings.find((entry) => entry.value === wanted) || null

    const { record: player, match, ambiguous } = resolvePlayer(base, playerIndex)

    // What this player already has on this innings' scorecard, if anything. Missing means they
    // are not in the squad yet, which the import fixes rather than refuses.
    const scorecardRow = inning && player ? inning.rowsByPlayer.get(String(player.id)) : undefined

    const { values, invalidCells } = parseStatCells(base.cells, statColumns)
    const valueCount = Object.keys(values).length

    const claimKey = inning && player ? `${inning.value}:${player.id}` : ''

    let result = 'valid'
    if (!inning) {
      result = 'unknown_inning'
    } else if (!player) {
      result = ambiguous ? 'ambiguous_player' : 'unmatched_player'
    } else if (claimed.has(claimKey)) {
      result = 'duplicate'
    } else if (!valueCount) {
      result = 'no_stats'
    }

    if (result === 'valid') claimed.add(claimKey)

    return {
      ...base,
      row: i + 1,
      inning: inning?.value ?? null,
      inning_label: inning?.label || '',
      player_id: player ? String(player.id) : '',
      player_name: player?.full_name || '',
      player_position: player?.position,
      match,
      ambiguous_count: ambiguous?.length || 0,
      values,
      invalid_cells: invalidCells,
      in_squad: Boolean(scorecardRow),
      overwrite: Boolean(scorecardRow && hasScores(scorecardRow, columns)),
      result,
      included: result === 'valid',
    }
  })
}

const ScorecardImport = ({ columns, players, innings, onImport, onCancel }) => {
  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [busy, setBusy] = useState(false)

  const [rows, setRows] = useState([])
  const [statColumns, setStatColumns] = useState([])
  const [ignoredHeaders, setIgnoredHeaders] = useState([])
  const [inningsColumn, setInningsColumn] = useState('')

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  const squadColumns = useMemo(() => (columns || []).filter((c) => c?.attribute_key), [columns])

  // One index over every player in the game - a file row is matched against all of them, and the
  // dropdown offers all of them, whether or not they are in this fixture's squad yet.
  const playerPool = useMemo(() => players || [], [players])
  const playerIndex = useMemo(
    () =>
      buildPlayerIndex(playerPool, {
        getKey: (player) => player.id,
        getPlayerId: (player) => player.id,
        getName: (player) => player.full_name,
      }),
    [playerPool],
  )

  const playerOptions = useMemo(
    () =>
      playerPool
        .map((player) => ({ value: String(player.id), label: playerLabel(player) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [playerPool],
  )

  // What each innings already holds, by player: the row a figure would overwrite, and whether the
  // player is in the squad for it at all.
  const inningsList = useMemo(
    () =>
      (innings || []).map((entry) => {
        const squad = entry.squad || []
        return {
          ...entry,
          squad,
          rowsByPlayer: new Map(squad.map((row) => [String(row.player_id), row])),
        }
      }),
    [innings],
  )

  const multiInnings = inningsList.length > 1

  // The squad is what a template is normally filled in for; before one has been picked it falls
  // back to the game's players, since the import can build the squad out of the file anyway.
  const templateRows = useMemo(() => {
    const squad = inningsList[0]?.squad || []
    return squad.length
      ? squad.map((row) => ({ name: row.player_name, id: row.player_id }))
      : playerPool.map((player) => ({ name: player.full_name, id: player.id }))
  }, [inningsList, playerPool])

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setRows([])
    setStatColumns([])
    setIgnoredHeaders([])
    setInningsColumn('')
    setImportErrors([])
  }

  // Built here rather than fetched: the template is these players crossed with the columns this
  // game actually scores, both of which are already on screen.
  const handleDownloadTemplate = () => {
    // Same column order a match export uses, so a template and an export are interchangeable
    // here. A 2-day fixture gets the Innings column and every player once per innings, which is
    // how a match export of both innings comes out.
    const header = [
      'Player',
      ...(multiInnings ? ['Innings'] : []),
      ...squadColumns.map((c) => c.label),
      'Player ID',
    ]
    const body = (multiInnings ? inningsList : inningsList.slice(0, 1)).flatMap((entry) =>
      templateRows.map((player) => [
        player.name,
        ...(multiInnings ? [entry.value] : []),
        ...squadColumns.map(() => ''),
        player.id ?? '',
      ]),
    )
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body])
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, sheet, 'Scorecard')
    XLSX.writeFile(book, 'MyClubtap-ScorecardImport.csv', { bookType: 'csv' })
  }

  const handleFile = (file) => {
    if (!file) return
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        const isCsv = /\.csv$/i.test(file.name)
        const workbook = XLSX.read(evt.target.result, { type: isCsv ? 'string' : 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      const read = readSheet(json, rawHeaders, squadColumns, multiInnings)
      if (read.error) {
        ToastComponent(read.error, 'error')
        setBusy(false)
        return
      }
      const {
        baseRows,
        detectedColumns,
        inningsColumn: inningsHeader,
        ignoredHeaders: ignored,
      } = read

      setFileName(file.name)
      setFileRowCount(baseRows.length)
      setStatColumns(detectedColumns)
      setIgnoredHeaders(ignored)
      setInningsColumn(inningsHeader)
      setRows(buildValidatedRows(baseRows, detectedColumns, inningsList, playerIndex, squadColumns))
      setImportErrors([])
      setBusy(false)
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  }

  const patchRow = (row, patch) => {
    setRows((prev) => {
      const baseRows = prev.map((r) => (r.row === row ? { ...r, ...patch } : r))
      const revalidated = buildValidatedRows(
        baseRows,
        statColumns,
        inningsList,
        playerIndex,
        squadColumns,
      )
      // A row the admin has just pointed somewhere comes back included; every other row keeps
      // the include/exclude decision already made for it.
      return revalidated.map((r, idx) => ({
        ...r,
        included: r.result === 'valid' && (r.row === row || prev[idx].included),
      }))
    })
  }

  const remapRow = (row, playerRowId) => patchRow(row, { manual_id: playerRowId })

  // The player a row points at is the same player in either innings, so moving a row between them
  // keeps a mapping made by hand - only what it is checked against changes.
  const remapInning = (row, inning) => patchRow(row, { manual_inning: inning })

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    return { valid, needsAttention: rows.length - valid }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)
  const overwriteCount = readyRows.filter((r) => r.overwrite).length
  const newToSquadCount = new Set(readyRows.filter((r) => !r.in_squad).map((r) => r.player_id)).size
  const skippedCells = rows.reduce((total, r) => total + r.invalid_cells, 0)

  // Grouped by innings rather than sent as one list: each innings is saved separately on the
  // scorecard, and a row can only be written to the innings its player row belongs to.
  const readyGroups = inningsList
    .map((entry) => ({
      inning: entry.value,
      label: entry.label,
      rows: readyRows.filter((r) => r.inning === entry.value),
    }))
    .filter((group) => group.rows.length)

  // What the file turned out to hold, on the bar above the rows. A 2-day file is counted per
  // innings: how the rows split between the two is the first thing to check on one.
  const fileDetail = [
    `${fileRowCount} rows`,
    ...(multiInnings
      ? inningsList.map(
          (entry) => `${entry.label}: ${rows.filter((r) => r.inning === entry.value).length}`,
        )
      : []),
    statColumns.map((c) => c.label).join(', '),
  ].join(' · ')

  // Names where the scores are going on the review step: one innings, or both when the file
  // carries both.
  const importTargetLabel = multiInnings
    ? readyGroups.map((group) => group.label).join(' and ') || 'this match'
    : inningsList[0]?.label || 'this innings'

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const res = await onImport(
        readyGroups.map((group) => ({
          inning: group.inning,
          label: group.label,
          updates: group.rows.map((r) => ({
            playerId: r.player_id,
            playerName: r.player_name,
            values: r.values,
          })),
        })),
      )
      if (res?.success) {
        resetAll()
      } else {
        setImportErrors(
          Array.isArray(res?.errors) && res.errors.length
            ? res.errors
            : ['Something went wrong. Please try again.'],
        )
      }
    } catch (e) {
      setImportErrors(['Something went wrong. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  const renderUpload = () => (
    <>
      <ImportUploadStep
        templateHint={
          multiInnings
            ? 'The template lists this fixture’s squad once per innings, with the stat columns this game scores, ready to fill in.'
            : 'The template lists this fixture’s squad with the stat columns this game scores, ready to fill in.'
        }
        templateButton={
          <CButton
            type="button"
            color="primary"
            variant="outline"
            disabled={!templateRows.length}
            onClick={handleDownloadTemplate}
          >
            <CIcon icon={cilCloudDownload} className="me-1" />
            Download Scorecard Template
          </CButton>
        }
        requiredColumns={[
          <>
            Player <span className="text-medium-emphasis">(or First Name / Last Name)</span>
          </>,
          <>
            Player ID <span className="text-medium-emphasis">(optional, matched first)</span>
          </>,
          ...(multiInnings
            ? [
                <>
                  Innings{' '}
                  <span className="text-medium-emphasis">
                    (1 or 2, one row per player per innings)
                  </span>
                </>,
              ]
            : []),
          <>One column per stat, named as on the grid &mdash; RS, 4S, WKTS, CS&hellip;</>,
        ]}
        info={
          <>
            A match export can be dropped in as it comes: columns this scorecard does not use are
            ignored, and blank cells are left alone, so a player who only bowled keeps an empty
            batting line rather than a row of zeros. Rows are matched on player ID first and then on
            name &mdash; against every player in this game, not only the ones already in the squad,
            and anyone missing from the squad is added to it as the scores are saved. You can point
            any row at a different player on the next step.
            {multiInnings && (
              <>
                {' '}
                This is a 2-day fixture, so the file&rsquo;s <strong>Innings</strong> column decides
                which innings each row is saved to &mdash; both innings import in one pass,
                whichever innings is on screen behind this panel.
              </>
            )}
          </>
        }
        fileName={fileName}
        fileDetail={`${fileRowCount} row${fileRowCount === 1 ? '' : 's'} and ${
          statColumns.length
        } stat column${statColumns.length === 1 ? '' : 's'} detected`}
        busy={busy}
        onFile={handleFile}
        onRemove={resetAll}
      />

      <div className="d-flex justify-content-between mt-4">
        <CButton type="button" color="secondary" variant="outline" onClick={onCancel}>
          Cancel
        </CButton>
        <CButton type="button" color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review File
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <ImportFileBar fileName={fileName} detail={fileDetail} onReplace={resetAll} />

      {multiInnings && !inningsColumn && (
        <InfoBox color="warning">
          This file has no Innings column, so no row can be placed on its own. Set the innings on
          each row below, or replace the file with an export that carries one.
        </InfoBox>
      )}

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile
          label="Overwrites Saved Scores"
          value={overwriteCount}
          tone="info"
          icon={cilSync}
        />
      </div>

      {ignoredHeaders.length > 0 && (
        <InfoBox color="light">
          Ignored column{ignoredHeaders.length === 1 ? '' : 's'}:{' '}
          <strong>{ignoredHeaders.join(', ')}</strong>. Nothing in{' '}
          {ignoredHeaders.length === 1 ? 'it' : 'them'} is imported.
        </InfoBox>
      )}

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>Name In File</CTableHeaderCell>
                {multiInnings && (
                  <CTableHeaderCell style={{ minWidth: 150 }}>Innings</CTableHeaderCell>
                )}
                <CTableHeaderCell style={{ minWidth: 260 }}>Player</CTableHeaderCell>
                <CTableHeaderCell>Scores</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const matchMeta = MATCH_META[r.match]
                return (
                  <CTableRow
                    key={r.row}
                    style={r.result === 'valid' ? undefined : tintStyle(meta.accent, '0d')}
                  >
                    <CTableDataCell className="ps-4 text-medium-emphasis">{r.row}</CTableDataCell>

                    <CTableDataCell>
                      <div className="fw-semibold">{r.csv_name || '—'}</div>
                      {r.csv_player_id && <code className="small">{r.csv_player_id}</code>}
                    </CTableDataCell>

                    {multiInnings && (
                      <CTableDataCell>
                        <CFormSelect
                          size="sm"
                          value={r.inning ?? ''}
                          onChange={(e) => remapInning(r.row, e.target.value)}
                        >
                          <option value="">Select innings</option>
                          {inningsList.map((entry) => (
                            <option key={entry.value} value={entry.value}>
                              {entry.label}
                            </option>
                          ))}
                        </CFormSelect>
                        {r.csv_inning && (
                          <div className="small text-medium-emphasis mt-1">
                            File says &ldquo;{r.csv_inning}&rdquo;
                          </div>
                        )}
                      </CTableDataCell>
                    )}

                    <CTableDataCell>
                      <CFormSelect
                        size="sm"
                        value={r.player_id}
                        onChange={(e) => remapRow(r.row, e.target.value)}
                      >
                        {/* Clearing a row that was pointed somewhere by hand puts it back on
                            whatever the file matched by itself, rather than emptying it. */}
                        <option value="">
                          {r.manual_id ? 'Undo manual mapping' : 'Select player'}
                        </option>
                        {playerOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </CFormSelect>
                      {matchMeta && (
                        <CBadge
                          color={matchMeta.color}
                          shape="rounded-pill"
                          className="px-2 py-1 mt-1"
                        >
                          {matchMeta.label}
                        </CBadge>
                      )}
                      {r.result === 'ambiguous_player' && (
                        <div className="small text-medium-emphasis mt-1">
                          {r.ambiguous_count} players in this game share this name — pick the right
                          one.
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      <RowScores statColumns={statColumns} values={r.values} />
                      {r.invalid_cells > 0 && (
                        <div className="small text-warning-emphasis">
                          {r.invalid_cells} cell{r.invalid_cells === 1 ? '' : 's'} could not be read
                          and {r.invalid_cells === 1 ? 'was' : 'were'} skipped.
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      <CBadge color={meta.color} shape="rounded-pill" className="px-2 py-1">
                        {meta.label}
                      </CBadge>
                      {r.overwrite && (
                        <div>
                          <CBadge
                            color="info"
                            shape="rounded-pill"
                            className="px-2 py-1 mt-1"
                            title="This player already has scores saved for this innings"
                          >
                            Overwrites
                          </CBadge>
                        </div>
                      )}
                      {r.player_id && !r.in_squad && (
                        <div>
                          <CBadge
                            color="primary"
                            shape="rounded-pill"
                            className="px-2 py-1 mt-1"
                            title="This player is not in the squad for this fixture yet, and is added by the import"
                          >
                            Adds To Squad
                          </CBadge>
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      {r.result === 'valid' && (
                        <CButton
                          type="button"
                          size="sm"
                          color={r.included ? 'danger' : 'success'}
                          variant="outline"
                          title={r.included ? 'Exclude from import' : 'Include in import'}
                          onClick={() => toggleRowIncluded(r.row)}
                        >
                          {r.included ? 'Exclude' : 'Include'}
                        </CButton>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      {summary.needsAttention > 0 && (
        <InfoBox color="light">
          Rows that need attention are left out of the import. Point one at the right squad player
          to fix it, or leave it — the rest will still import.
        </InfoBox>
      )}

      <div className="d-flex justify-content-between mt-3">
        <CButton type="button" color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CButton
          type="button"
          color="primary"
          disabled={!readyRows.length}
          onClick={() => goToStep(3)}
        >
          Continue
        </CButton>
      </div>
    </>
  )

  const renderReview = () => (
    <>
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
        <StatTile
          label="Ready to Import"
          value={readyRows.length}
          tone="success"
          icon={cilCheckCircle}
        />
        <StatTile
          label="Excluded"
          value={rows.length - readyRows.length}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile
          label="Overwrites Saved Scores"
          value={overwriteCount}
          tone="info"
          icon={cilSync}
        />
        {newToSquadCount > 0 && (
          <StatTile
            label="Added To Squad"
            value={newToSquadCount}
            tone="secondary"
            icon={cilPeople}
          />
        )}
      </div>

      <InfoBox>
        {newToSquadCount > 0 && (
          <>
            {newToSquadCount} player{newToSquadCount === 1 ? '' : 's'} in this file{' '}
            {newToSquadCount === 1 ? 'is' : 'are'} not in the squad for this fixture yet and{' '}
            {newToSquadCount === 1 ? 'is' : 'are'} added to it as part of the import.{' '}
          </>
        )}
        Importing saves these scores to {importTargetLabel} straight away and recalculates fantasy
        points — there is no separate save step.
        {skippedCells > 0 && (
          <>
            {' '}
            {skippedCells} cell{skippedCells === 1 ? '' : 's'} in the file could not be read and{' '}
            {skippedCells === 1 ? 'is' : 'are'} left as {skippedCells === 1 ? 'it is' : 'they are'}{' '}
            on the scorecard.
          </>
        )}
      </InfoBox>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Player</CTableHeaderCell>
                {statColumns.map((c) => (
                  <CTableHeaderCell key={c.attribute_key} title={c.full_name}>
                    {c.label}
                  </CTableHeaderCell>
                ))}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {readyGroups.map((group) => (
                <React.Fragment key={group.inning}>
                  {/* Banded by innings rather than mixed together: the two innings are saved
                      separately, and an admin checking a 2-day import is checking one innings at
                      a time. */}
                  {multiInnings && (
                    <CTableRow>
                      <CTableDataCell
                        colSpan={statColumns.length + 1}
                        className="ps-4 fw-semibold"
                        style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}
                      >
                        {group.label} &middot; {group.rows.length} player
                        {group.rows.length === 1 ? '' : 's'}
                      </CTableDataCell>
                    </CTableRow>
                  )}
                  {group.rows.map((r) => (
                    <CTableRow key={r.row}>
                      <CTableDataCell className="ps-4">
                        <div className="fw-semibold">{r.player_name}</div>
                        {r.overwrite && (
                          <div className="small text-medium-emphasis">Replaces saved scores</div>
                        )}
                        {!r.in_squad && (
                          <div className="small text-medium-emphasis">Added to the squad</div>
                        )}
                      </CTableDataCell>
                      {statColumns.map((c) => (
                        <CTableDataCell key={c.attribute_key}>
                          <DisplayValue
                            attributeKey={c.attribute_key}
                            value={r.values[c.attribute_key]}
                          />
                        </CTableDataCell>
                      ))}
                    </CTableRow>
                  ))}
                </React.Fragment>
              ))}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="d-flex justify-content-between mt-3">
        <CButton type="button" color="secondary" variant="outline" onClick={() => goToStep(2)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CLoadingButton
          type="button"
          color="success"
          loading={importing}
          disabled={!readyRows.length}
          onClick={runImport}
        >
          <CIcon icon={cilCloudUpload} className="me-1" />
          Import {readyRows.length} {multiInnings ? 'Row' : 'Player'}
          {readyRows.length === 1 ? '' : 's'}
        </CLoadingButton>
      </div>
    </>
  )

  if (!playerPool.length) {
    return (
      <InfoBox color="light">
        This game has no players to import scores for yet. Add players to the game first.
      </InfoBox>
    )
  }

  return (
    <>
      <MiniStepper
        steps={WIZARD_STEPS}
        current={wizardStep}
        maxReached={maxWizardStep}
        onStepClick={goToStep}
      />
      <ImportErrorAlert errors={importErrors} />
      {wizardStep === 1 && renderUpload()}
      {wizardStep === 2 && renderValidate()}
      {wizardStep === 3 && renderReview()}
    </>
  )
}

export default ScorecardImport
